import { DOCUMENT, inject, Injectable } from '@angular/core';
import { Meta, Title } from '@angular/platform-browser';
import { environment } from '@env/environment';

const APP_NAME = 'Edutrip';
const SEPARATOR = ' - ';
const SITE_URL = environment.appUrl.replace(/\/+$/, '');
const DEFAULT_OG_IMAGE = '/assets/images/og-default.jpg';
const MAX_DESCRIPTION = 160;

export interface PageMeta {
  title?: string;
  description?: string;
  path?: string;
  image?: string;
  type?: string;
}

// Single place that writes the document title, SEO/social meta tags and JSON-LD blocks; pages with fetched data call it again once their content resolves.
@Injectable({ providedIn: 'root' })
export class SeoService {
  private readonly title = inject(Title);
  private readonly meta = inject(Meta);
  private readonly doc = inject(DOCUMENT);

  readonly siteUrl = SITE_URL;
  readonly appName = APP_NAME;

  // Applies the page's title and meta tags, including JSON-LD structured-data blocks.
  apply(page: PageMeta): void {
    const fullTitle = page.title ? `${APP_NAME}${SEPARATOR}${page.title}` : APP_NAME;
    const description = (page.description ?? '').trim();
    const url = SITE_URL + this.pathOnly(page.path ?? '');
    const image = this.absoluteUrl(page.image || DEFAULT_OG_IMAGE);
    const type = page.type || 'website';

    this.title.setTitle(fullTitle);

    this.setName('description', description);
    this.setProperty('og:site_name', APP_NAME);
    this.setProperty('og:type', type);
    this.setProperty('og:url', url);
    this.setProperty('og:title', fullTitle);
    this.setProperty('og:description', description);
    this.setProperty('og:image', image);
    this.setName('twitter:card', 'summary_large_image');
    this.setName('twitter:title', fullTitle);
    this.setName('twitter:description', description);
    this.setName('twitter:image', image);
  }

  // Sets or replaces a <script type="application/ld+json"> block by its key.
  setJsonLd(key: string, data: unknown): void {
    const id = `ld-json-${key}`;
    this.doc.getElementById(id)?.remove();
    if (data == null) return;
    const script = this.doc.createElement('script');
    script.type = 'application/ld+json';
    script.id = id;
    script.textContent = JSON.stringify(data);
    this.doc.head.appendChild(script);
  }

  // Removes a <script type="application/ld+json"> block by its key.
  removeJsonLd(key: string): void {
    this.doc.getElementById(`ld-json-${key}`)?.remove();
  }

  // Turns API rich text into a plain, trimmed meta-description string.
  toDescription(html: string, max = MAX_DESCRIPTION): string {
    const text = (html || '')
      .replace(/<[^>]*>/g, ' ')
      .replace(/&nbsp;/g, ' ')
      .replace(/&amp;/g, '&')
      .replace(/&#39;|&rsquo;|&lsquo;/g, "'")
      .replace(/&quot;|&ldquo;|&rdquo;/g, '"')
      .replace(/\s+/g, ' ')
      .trim();
    if (text.length <= max) return text;
    return text.slice(0, text.lastIndexOf(' ', max - 1) > 0 ? text.lastIndexOf(' ', max - 1) : max).trim() + '…';
  }

  // Converts a relative path to an absolute URL, using the site URL as the base.
  absoluteUrl(path: string): string {
    return /^https?:\/\//i.test(path) ? path : `${SITE_URL}/${path.replace(/^\/+/, '')}`;
  }

  // Strips the query string and fragment; '' for the site root.
  private pathOnly(url: string): string {
    const path = url.split(/[?#]/)[0];
    return path === '/' ? '' : path;
  }

  // Sets a `name=` meta tag, removing it when the content is empty.
  private setName(name: string, content: string): void {
    if (content) this.meta.updateTag({ name, content });
    else this.meta.removeTag(`name="${name}"`);
  }

  // Sets a `property=` meta tag, removing it when the content is empty.
  private setProperty(property: string, content: string): void {
    if (content) this.meta.updateTag({ property, content });
    else this.meta.removeTag(`property="${property}"`);
  }
}
